import React, { useState, useEffect, useMemo } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import {
  Package, Truck, DollarSign, Users, Headphones,
  Settings, BarChart2, TrendingUp, ChevronDown, ChevronRight,
  LogOut, Bot, Building
} from 'lucide-react';

// -- localStorage key for persistent sidebar state ---------------------------
const LS_KEY = 'cargopulse.sidebar.expanded.v1';

// -- Nav structure -----------------------------------------------------------
const NAV = [
  {
    id: 'ops', label: 'Operations', icon: Truck,
    roles: ['ADMIN', 'STAFF', 'RIDER', 'MANAGEMENT'],
    items: [
      { label: 'Dashboard',      to: '/ops' },
      { label: 'Book Shipment',  to: '/ops/booking' },
      { label: 'Scan Terminal',  to: '/ops/scan' },
      { label: 'All Shipments',  to: '/ops/shipments' },
      { label: 'Pickup Assign',  to: '/ops/pickup-assign' },
      { label: 'Delivery Sheet', to: '/ops/delivery-sheet' },
      { label: 'Manifests',      to: '/ops/manifests' },
      { label: 'Exceptions',     to: '/ops/exceptions' },
      { label: 'COD Settlement', to: '/ops/cash-settlement' },
    ],
  },
  {
    id: 'warehouse', label: 'Warehouse & Intl', icon: Building,
    roles: ['ADMIN', 'STAFF', 'MANAGEMENT'],
    items: [
      { label: 'Warehouse',     to: '/warehouse' },
      { label: 'International', to: '/international' },
    ],
  },
  {
    id: 'management', label: 'Management', icon: BarChart2,
    roles: ['ADMIN', 'MANAGEMENT'],
    items: [
      { label: 'Company Portal',    to: '/portal' },
      { label: 'Exec Dashboard',    to: '/management' },
      { label: 'Revenue Analytics', to: '/management/revenue' },
      { label: 'Performance',       to: '/management/performance' },
      { label: 'Exceptions',        to: '/management/exceptions' },
      { label: 'Scenario Analysis', to: '/management/scenario' },
      { label: 'Approvals',         to: '/approvals' },
      { label: 'Branch KPIs',       to: '/portal/branches' },
      { label: 'MIS Reports',       to: '/reports/mis' },
    ],
  },
  {
    id: 'sales', label: 'Sales & CRM', icon: TrendingUp,
    roles: ['ADMIN', 'SALES', 'MANAGEMENT'],
    items: [
      { label: 'Sales Dashboard', to: '/sales' },
      { label: 'Lead Pipeline',   to: '/sales/leads' },
      { label: 'Corporate Accts', to: '/sales/corporate' },
      { label: 'Sales Targets',   to: '/sales/targets' },
    ],
  },
  {
    id: 'finance', label: 'Finance & Accounts', icon: DollarSign,
    roles: ['ADMIN', 'FINANCE', 'MANAGEMENT'],
    items: [
      { label: 'Dashboard',           to: '/finance' },
      { label: 'Invoices',            to: '/finance/invoices' },
      { label: 'Vouchers',            to: '/finance/vouchers' },
      { label: 'Chart of Accounts',   to: '/finance/coa' },
      { label: 'Trial Balance',       to: '/finance/trial-balance' },
      { label: 'Cash Book',           to: '/finance/cash-book' },
      { label: 'Bank Book',           to: '/finance/bank-book' },
      { label: 'AR Aging',            to: '/finance/aging' },
      { label: 'P&L Report',          to: '/finance/pnl' },
      { label: 'COD Remittance',      to: '/finance/cod' },
      { label: 'Refunds',             to: '/finance/refunds' },
      { label: 'Bank Reconciliation', to: '/finance/bank-recon' },
    ],
  },
  {
    id: 'hr', label: 'Human Resources', icon: Users,
    roles: ['ADMIN', 'HR'],
    items: [
      { label: 'Employees',      to: '/hr/employees' },
      { label: 'Attendance',     to: '/hr/attendance' },
      { label: 'Payroll',        to: '/hr/payroll' },
      { label: 'Payslips',       to: '/hr/payslips' },
      { label: 'Leave',          to: '/hr/leave' },
      { label: 'Recruitment',    to: '/hr/recruitment' },
      { label: 'Appraisals',     to: '/hr/appraisals' },
      { label: 'Expense Claims', to: '/hr/expenses' },
    ],
  },
  {
    id: 'cs', label: 'Customer Service', icon: Headphones,
    roles: ['ADMIN', 'CS', 'MANAGEMENT'],
    items: [
      { label: 'Tickets',       to: '/cs/tickets' },
      { label: 'Claims',        to: '/cs/claims' },
      { label: 'SLA Dashboard', to: '/cs/sla' },
      { label: 'Comm Logs',     to: '/cs/comm-logs' },
    ],
  },
  {
    id: 'ai', label: 'AI & Analytics', icon: Bot,
    roles: ['ADMIN', 'MANAGEMENT'],
    items: [
      { label: 'AI Dashboard',    to: '/ai' },
      { label: 'Route Optimiser', to: '/ai/routes' },
      { label: 'Fraud Alerts',    to: '/ai/fraud' },
      { label: 'EDD Predictor',   to: '/ai/edd' },
      { label: 'Hub Capacity',    to: '/ai/capacity' },
    ],
  },
  {
    id: 'admin', label: 'Administration', icon: Settings,
    roles: ['ADMIN'],
    items: [
      { label: 'Hubs & Zones',     to: '/admin/hubs' },
      { label: 'Zones',            to: '/admin/zones' },
      { label: 'Service Areas',    to: '/admin/areas' },
      { label: 'Tariffs',          to: '/admin/tariffs' },
      { label: 'Users',            to: '/admin/users' },
      { label: 'Role Permissions', to: '/admin/roles' },
      { label: 'Workflows',        to: '/admin/workflows' },
      { label: 'Notif Settings',   to: '/admin/notif-settings' },
      { label: 'Integrations',     to: '/admin/integrations' },
      { label: 'Audit Log',        to: '/admin/audit' },
      { label: 'Security',         to: '/admin/security' },
      { label: 'Notifications',    to: '/notifications' },
      { label: 'Settings',         to: '/admin/settings' },
    ],
  },
];

// -- Helpers ----------------------------------------------------------------
function loadFromStorage() {
  try {
    const raw = localStorage.getItem(LS_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

function saveToStorage(state) {
  try { localStorage.setItem(LS_KEY, JSON.stringify(state)); } catch {}
}

// Find which section contains the current path (for auto-expand)
function findActiveSection(pathname) {
  for (const section of NAV) {
    if (section.items.some(item =>
      pathname === item.to || pathname.startsWith(item.to + '/'))
    ) {
      return section.id;
    }
  }
  return null;
}

// -- Sidebar component ------------------------------------------------------
export default function Sidebar({ isOpen, onClose }) {
  const { user, userType, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  // expanded state: starts from localStorage, gets active section auto-merged
  const [expanded, setExpanded] = useState(() => loadFromStorage());

  // Auto-expand the section containing the current page on every route change.
  // This is non-destructive: we only set true, we never collapse user-expanded ones.
  useEffect(() => {
    const activeId = findActiveSection(location.pathname);
    if (activeId && !expanded[activeId]) {
      setExpanded(prev => {
        const next = { ...prev, [activeId]: true };
        saveToStorage(next);
        return next;
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  // User toggle: persists to localStorage immediately
  const toggle = (id) => {
    setExpanded(prev => {
      const next = { ...prev, [id]: !prev[id] };
      saveToStorage(next);
      return next;
    });
  };

  const isActive = (to) =>
    location.pathname === to || location.pathname.startsWith(to + '/');

  const handleLogout = () => {
    logout();
    // clear sidebar state on logout so next user starts clean
    try { localStorage.removeItem(LS_KEY); } catch {}
    navigate('/login');
    onClose?.();
  };

  const role = user?.role || userType;
  const visibleNav = useMemo(
    () => NAV.filter(s => !s.roles || s.roles.includes(role)),
    [role]
  );

  return (
    <aside
      style={{
        position: 'fixed',
        top: 0, left: 0,
        height: '100vh',
        width: 240,
        background: 'var(--paper, #FFFFFF)',
        borderRight: '1px solid var(--line, #E5E7EB)',
        display: 'flex',
        flexDirection: 'column',
        zIndex: 1000,
        overflowY: 'auto',
        overflowX: 'hidden',
        transition: 'transform 0.25s ease',
        transform: isOpen ? 'translateX(0)' : undefined,
        WebkitOverflowScrolling: 'touch',
      }}
      className={`sidebar${isOpen ? ' open' : ''}`}
    >
      {/* -- Logo -- */}
      <div style={{
        padding: '18px 16px',
        borderBottom: '1px solid var(--line, #E5E7EB)',
        display: 'flex', alignItems: 'center', gap: 10,
        flexShrink: 0,
      }}>
        <span style={{ fontSize: 22, color: 'var(--amber, #F59E0B)' }}>{'\u26A1'}</span>
        <div style={{ flex: 1 }}>
          <div style={{
            color: 'var(--navy, #0A1838)',
            fontFamily: 'var(--font-display, "DM Serif Display", serif)',
            fontWeight: 400,
            fontSize: '1.15rem',
            lineHeight: 1.1,
            letterSpacing: '-0.01em',
          }}>
            CargoPulse
          </div>
          <div style={{ color: 'var(--muted, #6B7280)', fontSize: '0.7rem', marginTop: 2 }}>
            Courier &amp; Cargo
          </div>
        </div>
        <button
          onClick={onClose}
          className="show-mobile"
          style={{
            background: 'none', border: 'none',
            color: 'var(--muted, #6B7280)', cursor: 'pointer',
            padding: 4, borderRadius: 6, lineHeight: 1,
          }}
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
            <path d="M18 6L6 18M6 6l12 12" />
          </svg>
        </button>
      </div>

      {/* -- Nav -- */}
      <nav style={{ flex: 1, padding: '8px 0', overflowY: 'auto' }}>
        {visibleNav.map(section => {
          const Icon = section.icon;
          const open = !!expanded[section.id];
          const sectionActive = section.items.some(i => isActive(i.to));

          return (
            <div key={section.id}>
              <div
                onClick={() => toggle(section.id)}
                style={{
                  display: 'flex', alignItems: 'center', gap: 10,
                  padding: '10px 16px',
                  cursor: 'pointer',
                  color: sectionActive ? 'var(--navy, #0A1838)' : 'var(--muted, #6B7280)',
                  background: sectionActive ? 'var(--cream-warm, #F1ECDF)' : 'transparent',
                  borderLeft: sectionActive
                    ? '3px solid var(--amber, #F59E0B)'
                    : '3px solid transparent',
                  transition: 'all 0.15s',
                  userSelect: 'none',
                  minHeight: 44,
                  fontWeight: sectionActive ? 600 : 500,
                }}
              >
                <Icon size={17} style={{ flexShrink: 0 }} />
                <span style={{ flex: 1, fontSize: '0.875rem' }}>
                  {section.label}
                </span>
                {open
                  ? <ChevronDown size={14} />
                  : <ChevronRight size={14} />
                }
              </div>

              {open && (
                <div style={{ background: 'var(--cream-dim, #F5F1E8)' }}>
                  {section.items.map(item => {
                    const active = isActive(item.to);
                    return (
                      <Link
                        key={item.to}
                        to={item.to}
                        onClick={onClose}
                        style={{
                          display: 'flex', alignItems: 'center',
                          padding: '8px 16px 8px 42px',
                          minHeight: 40,
                          fontSize: '0.82rem',
                          color: active ? 'var(--paper, #FFFFFF)' : 'var(--ink-soft, #374151)',
                          background: active ? 'var(--navy, #0A1838)' : 'transparent',
                          borderLeft: active
                            ? '3px solid var(--amber, #F59E0B)'
                            : '3px solid transparent',
                          textDecoration: 'none',
                          transition: 'all 0.1s',
                          fontWeight: active ? 600 : 400,
                        }}
                      >
                        {item.label}
                      </Link>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </nav>

      {/* -- User + Logout -- */}
      <div style={{
        borderTop: '1px solid var(--line, #E5E7EB)',
        padding: '14px 16px',
        flexShrink: 0,
        background: 'var(--cream-dim, #F5F1E8)',
      }}>
        <div style={{
          color: 'var(--navy, #0A1838)',
          fontSize: '0.85rem',
          fontWeight: 600,
        }}>
          {user?.name || 'User'}
        </div>
        <div style={{
          color: 'var(--muted, #6B7280)',
          fontSize: '0.72rem',
          marginBottom: 10,
          textTransform: 'uppercase',
          letterSpacing: '0.05em',
        }}>
          {user?.role || role}
        </div>
        <button
          onClick={handleLogout}
          style={{
            display: 'flex', alignItems: 'center', gap: 8,
            width: '100%', padding: '8px 12px',
            background: 'transparent',
            border: '1px solid var(--line, #E5E7EB)',
            borderRadius: 8,
            color: 'var(--danger, #EF4444)',
            cursor: 'pointer',
            fontSize: '0.82rem',
            fontWeight: 600,
            minHeight: 40,
            transition: 'all 0.15s',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = 'var(--danger-bg, #FEE2E2)';
            e.currentTarget.style.borderColor = 'var(--danger, #EF4444)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = 'transparent';
            e.currentTarget.style.borderColor = 'var(--line, #E5E7EB)';
          }}
        >
          <LogOut size={15} /> Logout
        </button>
      </div>
    </aside>
  );
}
